import * as React from 'react';
import { ReferenceLine } from 'recharts';
import {
  TooltipProvider as ShadcnTooltipProvider,
  Tooltip as ShadcnTooltip,
  TooltipTrigger as ShadcnTooltipTrigger,
  TooltipContent as ShadcnTooltipContent,
} from '@/hen-ui/shadcn/tooltip';
import { warnOnce } from '@/hen-ui/utils';
import {
  MINIMAL_MARKDOWN_LINK_CLASS_NAME,
  parseMinimalMarkdown,
} from '@/hen-ui/lib/minimal-markdown';
import type {
  ChartDataPoint,
  ChartConfig,
  ReferenceLineConfig,
  ChartTooltipMode,
  ChartTooltipFooter,
  XAxisInterval,
  ScatterChartDatum,
  ScatterChartSeries,
} from './chart-types';
import {
  truncateWithEllipsis,
  MAX_LABEL_LENGTH_BEFORE_TRUNCATION,
} from '@/hen-ui/utils/chart';

/**
 * Bottom margin (in px) added to Recharts chart area when the legend is
 * hidden (`showLegend={false}`) but an X-axis label is present. Prevents
 * the label from being clipped by the container boundary.
 *
 * Recharts reserves bottom space automatically when a legend is rendered;
 * this constant provides the equivalent space when the legend is omitted.
 */
export const X_AXIS_LABEL_MARGIN_PX = 20;

/**
 * Consumers (Storybook, React strict mode, frozen Redux state) may pass
 * immutable objects. This helper returns shallow copies of every array-typed
 * prop so Recharts can mutate freely without throwing.
 *
 * Pure function — no hooks, safe to call at any point in render. Kept as a
 * single call-site per component so the copy rules stay in one place.
 *
 * Invariant: `ChartDataPoint = Record<string, string | number | null>` — all
 * row values are primitives, so `{ ...row }` fully clones each row. If the
 * type ever widens to allow arrays or nested objects as cell values (e.g. to
 * carry tooltip payloads), this helper must switch to a deep clone or the
 * caller must clone per-field. Same applies to `yAxisDomain` / `yAxisTicks`:
 * both are arrays of numbers, so a spread is a complete copy.
 */
export function getSafeChartProps<
  T extends {
    data: ChartDataPoint[];
    yAxisDomain?: [number, number];
    yAxisTicks?: number[];
  },
>(props: T): T {
  const { data, yAxisDomain, yAxisTicks, ...rest } = props;

  return {
    ...rest,
    data: data.map((row) => ({ ...row })),
    yAxisDomain: yAxisDomain
      ? ([...yAxisDomain] as [number, number])
      : undefined,
    yAxisTicks: yAxisTicks ? [...yAxisTicks] : undefined,
  } as T;
}

/**
 * Normalize a consumer-supplied tick array: drop non-numbers / NaN / ±Infinity,
 * dedupe, and sort ascending. Returns `undefined` when the input is not an
 * array OR resolves to an empty set after filtering, so downstream code falls
 * back to auto-derived ticks (rather than Recharts rendering zero ticks for
 * `ticks={[]}`).
 *
 * When `chartName` is provided, emits a one-time `console.warn` per dropped
 * entry (keyed by chart + index + value via `warnOnce`) so config typos
 * surface in dev without spamming the console on every re-render. Without
 * `chartName`, the helper drops silently — preserves the original behavior
 * for callers that haven't opted in.
 *
 * Hoisted as a module-level function so it allocates once per load rather
 * than per render. Pure — safe to call inside `useMemo`.
 *
 * EDUN-12034 contract:
 *   - `undefined` / non-array  → `undefined` (auto-ticks)
 *   - `[]`                     → `undefined` (auto-ticks)
 *   - `[NaN, 'x', null, 10]`   → `[10]` + 3 warns (when chartName set)
 *   - `[20, 10, 10, 30]`       → `[10, 20, 30]` (dedupe + sort)
 *   - all-invalid              → `undefined` (auto-ticks) + per-entry warns
 */
export function sanitizeTicks(
  ticks: number[] | undefined,
  chartName?: string,
): number[] | undefined {
  if (!Array.isArray(ticks)) return undefined;

  const cleaned: number[] = [];
  ticks.forEach((value, index) => {
    if (typeof value === 'number' && Number.isFinite(value)) {
      cleaned.push(value);
      return;
    }
    if (chartName) {
      // Distinct label per dropped-value category so warns and warnOnce
      // keys differentiate `NaN` from `null`, `Infinity` from `null`, etc.
      //
      // `JSON.stringify(NaN)` and `JSON.stringify(±Infinity)` both produce
      // the literal string `"null"` — which would (a) misreport the value
      // in the warn message and (b) collide on the warnOnce key with an
      // actual `null` entry at the same index, silently swallowing the
      // second warning. Short-circuit to `String(value)` for numeric
      // non-finites so each category gets a unique label ('NaN' /
      // 'Infinity' / '-Infinity'), and reserve `JSON.stringify` for the
      // non-number branch where it gives the most informative output
      // (`"x"` for strings, `null` for null, `{"v":1}` for objects).
      //
      // Two additional fallbacks for the non-number branch:
      //   - `JSON.stringify` returns `undefined` (NOT throws) for symbols,
      //     functions, and bigint values it can't represent. Fall through
      //     to `String(value)` so the label still distinguishes the entry
      //     (`"Symbol(sym)"`, `"function foo() {…}"`) instead of collapsing
      //     every un-stringifiable entry to the literal `"undefined"`.
      //   - `JSON.stringify` throws `TypeError` on circular references.
      //     `String(value)` also returns something usable here
      //     (`"[object Object]"`), so the catch keeps the warn fired.
      let valueLabel: string;
      if (typeof value === 'number') {
        valueLabel = String(value);
      } else {
        let stringified: string | undefined;
        try {
          stringified = JSON.stringify(value);
        } catch {
          stringified = undefined;
        }
        valueLabel = stringified ?? String(value);
      }
      warnOnce(
        `y-axis-ticks-invalid-${chartName}-${index}-${valueLabel}`,
        `[hen-ui/${chartName}] yAxisTicks[${index}]=${valueLabel} is not a finite number; dropping it.`,
      );
    }
  });

  if (cleaned.length === 0) return undefined;
  return Array.from(new Set(cleaned)).sort((a, b) => a - b);
}

/**
 * Sanitize a single value into a CSS-custom-property-safe token.
 *
 * hen-ui colors each series by emitting a `--color-<key>` custom property and
 * referencing `var(--color-<key>)` for the mark + legend swatch. CSS custom
 * property names cannot contain whitespace and choke on most punctuation, so a
 * human-readable series key like `"Fall 2026"` would mint an invalid
 * `--color-Fall 2026` declaration — the browser drops it and the series
 * renders black with an empty legend swatch. (Column-keyed charts like
 * Bar/Line never hit this because their keys are already identifiers such as
 * `submitted_apps`; scatter keys its series by `colorGroupBy` data values.)
 *
 * Collapses every run of non-`[A-Za-z0-9_-]` characters to a single `-`,
 * trims leading/trailing dashes, and falls back to the literal `"series"`
 * when nothing printable survives (all-punctuation / all-emoji), so the
 * result is always a non-empty, declaration-safe token.
 *
 *   "Fall 2026"   → "Fall-2026"
 *   "GPA ≥ 3.5"   → "GPA-3-5"
 *   "  spaced  "  → "spaced"
 *   "🎓"          → "series"
 *
 * Pure — safe to call inside `useMemo` / render.
 */
export function toCssVarSafeKey(value: string): string {
  const cleaned = value
    .replace(/[^A-Za-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return cleaned.length > 0 ? cleaned : 'series';
}

/**
 * Build a stable, collision-free `rawValue → cssSafeKey` map for a set of
 * series values, preserving first-seen insertion order.
 *
 * Two distinct raw values can sanitize to the same base token (e.g.
 * `"Fall 2026"` and `"Fall/2026"` both → `"Fall-2026"`). To keep each series
 * independently addressable by its own `--color-<key>`, collisions get a
 * numeric suffix in encounter order: the first claims the bare token, the
 * next becomes `"<base>-2"`, then `"<base>-3"`, and so on. Duplicate raw
 * values map once (first occurrence) and are skipped thereafter.
 *
 *   ["Fall 2026", "Fall/2026", "Spring 2027"]
 *     → { "Fall 2026"  → "Fall-2026",
 *         "Fall/2026"  → "Fall-2026-2",
 *         "Spring 2027" → "Spring-2027" }
 *
 * Returned keys are guaranteed unique, so they can be used directly as
 * ChartConfig keys, `<Scatter name>` identifiers, and `var(--color-<key>)`
 * references without clobbering one another.
 *
 * Pure — safe to call inside `useMemo` / render.
 */
export function buildSafeSeriesKeyMap(
  values: readonly string[],
): Map<string, string> {
  const used = new Set<string>();
  const map = new Map<string, string>();
  for (const value of values) {
    if (map.has(value)) continue;
    const base = toCssVarSafeKey(value);
    let key = base;
    let n = 2;
    while (used.has(key)) {
      key = `${base}-${n}`;
      n += 1;
    }
    used.add(key);
    map.set(value, key);
  }
  return map;
}

/**
 * Dev-only sanity check: if any entry in `series` is missing from `config`,
 * the chart renders with an empty CSS color variable and the raw key as its
 * legend label. This is easy to miss when `config` is built dynamically
 * (e.g. from a dashboard manifest).
 *
 * Hook form so we can dedupe by component instance: we cache the set of
 * missing-key signatures already warned about in a `useRef`, matching
 * React's own one-shot-per-instance dev-warning cadence. Without this,
 * dashboards that re-render on streaming data spam the console with the
 * same warning on every tick. A brand new missing key (e.g. config/series
 * drift mid-session) still triggers a fresh warning.
 *
 * No-op in production so the hook tree-shakes out of the release bundle.
 */
export function useMissingChartConfigWarning(
  componentName: string,
  config: ChartConfig,
  series: string[],
): void {
  const warnedSignatures = React.useRef<Set<string>>(new Set());

  if (process.env.NODE_ENV === 'production') return;

  const missing = series.filter((key) => !config[key]);
  if (missing.length === 0) return;

  const signature = missing.join('|');
  if (warnedSignatures.current.has(signature)) return;
  warnedSignatures.current.add(signature);

  console.warn(
    `[hen-ui/${componentName}] ChartConfig is missing entries for series keys: ${missing
      .map((k) => `"${k}"`)
      .join(
        ', ',
      )}. These series will render with empty --color-<key> CSS variables and the raw key as the legend label.`,
  );
}

/**
 * Resolve the effective tooltip value formatter for a chart composite.
 *
 * Contract: if the caller does not pass `tooltipValueFormat`, fall back to
 * `yAxisFormat`. A percentage Y-axis ("75%") must then produce a percentage
 * tooltip value automatically — this is the UNC Charlotte PRD requirement
 * that a tick of `0.75` renders as `"75%"` in both the axis and the tooltip.
 *
 * Signatures: `yAxisFormat: (v: number) => string` is assignable to
 * `(v: number, name: string | number) => string` because excess parameters
 * are permitted in function-type assignability (the extra `name` is simply
 * ignored by the axis formatter). Keep the fallback direction one-way —
 * never try to widen `tooltipValueFormat` back into an axis formatter, or
 * multi-series tooltips that branch on `name` will mis-label axis ticks.
 *
 * Extracted so both LineChart and BarChart delegate through a single named
 * function; any future PR that removes the `?? yAxisFormat` default has to
 * do so by editing this helper (and breaking its test), not by silently
 * dropping the fallback at the call site.
 */
export function resolveTooltipValueFormat(
  tooltipValueFormat:
    | ((value: number, name: string | number) => string)
    | undefined,
  yAxisFormat: ((value: number) => string) | undefined,
): ((value: number, name: string | number) => string) | undefined {
  return tooltipValueFormat ?? yAxisFormat;
}

// ─────────────────────────────────────────────────────────────────────────────
// X-Axis Configuration Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Minimum allowed tick count (below this the axis is unreadable). */
const X_AXIS_TICK_COUNT_MIN = 2;
/** Maximum allowed tick count (above this the axis is cluttered). */
const X_AXIS_TICK_COUNT_MAX = 50;

/** Valid string values for xAxisInterval. */
const VALID_INTERVAL_STRINGS = new Set([
  'preserveStart',
  'preserveEnd',
  'preserveStartEnd',
]);

/**
 * Validate and clamp `xAxisTickCount` to a sensible range.
 *
 * **Pure** — returns the validated value with no side effects. Use
 * `useXAxisConfigWarnings` (or the consolidated `useXAxisConfig`) to emit
 * warnings (rules of hooks: warnings must run in an effect, not during
 * render).
 *
 * Contract:
 * - `undefined` input               → `undefined` output (Recharts default).
 * - `NaN` / `±Infinity` input       → `undefined` output (treated as "not
 *   set" so a typo doesn't reach Recharts as `tickCount={NaN}`).
 * - Value below `X_AXIS_TICK_COUNT_MIN` → clamped to min.
 * - Value above `X_AXIS_TICK_COUNT_MAX` → clamped to max.
 */
export function validateXAxisTickCount(
  tickCount: number | undefined,
): number | undefined {
  if (tickCount === undefined) return undefined;
  // Guard against `NaN` / `±Infinity` — comparing NaN against the bounds
  // returns `false` in both directions, so without this check NaN would slip
  // through unchanged and reach Recharts.
  if (!Number.isFinite(tickCount)) return undefined;
  if (tickCount < X_AXIS_TICK_COUNT_MIN) return X_AXIS_TICK_COUNT_MIN;
  if (tickCount > X_AXIS_TICK_COUNT_MAX) return X_AXIS_TICK_COUNT_MAX;
  return tickCount;
}

/**
 * Validate `xAxisInterval` and return a safe value for Recharts.
 *
 * **Pure** — returns the validated value with no side effects. Use
 * `useXAxisConfigWarnings` to emit warnings.
 *
 * Contract:
 * - `undefined` input        → `undefined` output (let caller pick its own default).
 * - Numeric input            → coerced to `Math.max(0, Math.floor(n))` so
 *                              Recharts always receives a non-negative integer
 *                              (it interprets `interval={N}` as "show every
 *                              Nth label", so negative / fractional values are
 *                              meaningless).
 * - Non-finite number (NaN / ±Infinity) → `undefined` (defensive).
 * - Valid string literal     → passed through unchanged.
 * - Invalid string           → `undefined` (let Recharts fall back to its default).
 */
export function validateXAxisInterval(
  interval: XAxisInterval | undefined,
): XAxisInterval | undefined {
  if (interval === undefined) return undefined;
  if (typeof interval === 'number') {
    if (!Number.isFinite(interval)) return undefined;
    // `interval` means "show every Nth label" — negative / fractional values
    // have no sensible interpretation. Normalize before handing to Recharts.
    return Math.max(0, Math.floor(interval));
  }
  if (VALID_INTERVAL_STRINGS.has(interval)) return interval;
  return undefined;
}

/**
 * React hook that emits one-time warnings for invalid / conflicting X-axis
 * configuration. Runs in `useEffect` so render stays pure and React
 * StrictMode does not produce duplicate warnings.
 *
 * Mirrors `useInteractiveLegend`'s pattern of using `warnOnce` keyed by
 * chart name + symptom.
 *
 * The `xAxisType` arg is optional (defaults to `'category'`) so existing
 * callers that don't differentiate stay valid; pass it when you want the
 * "interval has no effect on numeric axes" warn to fire.
 */
export function useXAxisConfigWarnings({
  rawTickCount,
  rawInterval,
  xAxisType = 'category',
  chartName,
}: {
  rawTickCount: number | undefined;
  rawInterval: XAxisInterval | undefined;
  xAxisType?: 'category' | 'number';
  chartName: string;
}): void {
  React.useEffect(() => {
    if (rawTickCount !== undefined) {
      if (!Number.isFinite(rawTickCount)) {
        // `NaN` / `±Infinity` — surface as a separate warn so a stringified
        // DSL value (`"abc"` parsed to NaN) is distinguishable from an
        // out-of-range integer.
        warnOnce(
          `x-axis-tick-count-non-finite-${chartName}`,
          `[hen-ui/${chartName}] xAxisTickCount=${rawTickCount} is not a finite number. Falling back to Recharts default.`,
        );
      } else if (rawTickCount < X_AXIS_TICK_COUNT_MIN) {
        warnOnce(
          `x-axis-tick-count-min-${chartName}`,
          `[hen-ui/${chartName}] xAxisTickCount=${rawTickCount} is below minimum (${X_AXIS_TICK_COUNT_MIN}). Clamping to ${X_AXIS_TICK_COUNT_MIN}.`,
        );
      } else if (rawTickCount > X_AXIS_TICK_COUNT_MAX) {
        warnOnce(
          `x-axis-tick-count-max-${chartName}`,
          `[hen-ui/${chartName}] xAxisTickCount=${rawTickCount} exceeds maximum (${X_AXIS_TICK_COUNT_MAX}). Clamping to ${X_AXIS_TICK_COUNT_MAX}.`,
        );
      }
    }

    if (
      rawInterval !== undefined &&
      typeof rawInterval !== 'number' &&
      !VALID_INTERVAL_STRINGS.has(rawInterval)
    ) {
      warnOnce(
        `x-axis-interval-invalid-${chartName}`,
        `[hen-ui/${chartName}] xAxisInterval="${rawInterval}" is invalid. Valid values: number, 'preserveStart', 'preserveEnd', 'preserveStartEnd'. Falling back to Recharts default.`,
      );
    }

    // Numeric-axis no-op warn — Recharts derives tick positions from
    // `tickCount` on numeric X axes and ignores `interval` entirely. A
    // consumer experimenting with `xAxisInterval='preserveStartEnd'` on a
    // numeric axis would see no effect and assume the prop is broken;
    // surface the contract explicitly. `useXAxisConfig` also zeros out the
    // resolved `interval` for numeric axes so we don't silently pass a
    // dead value to Recharts.
    if (xAxisType === 'number' && rawInterval !== undefined) {
      warnOnce(
        `x-axis-interval-numeric-noop-${chartName}`,
        `[hen-ui/${chartName}] xAxisInterval has no effect when xAxisType="number" — Recharts uses tickCount-derived ticks on numeric axes. Use xAxisTickCount instead, or switch to xAxisType="category".`,
      );
    }

    // Category-axis no-op warn (symmetric to the numeric-noop warn above) —
    // Recharts ignores `tickCount` on category axes (it always emits one
    // tick wrapper per category). Worse, if the resolver drops the
    // `interval={0}` back-compat default to honor the doomed `tickCount`,
    // Recharts' own default `interval='preserveEnd'` then visibly thins
    // labels — making it LOOK like `tickCount` is working when it isn't.
    // `useXAxisConfig` keeps `interval={0}` intact on category axes so the
    // label set is unchanged; this warn tells the operator their
    // `tickCount` was dropped and points them to `xAxisInterval`.
    if (
      xAxisType !== 'number' &&
      rawTickCount !== undefined &&
      Number.isFinite(rawTickCount)
    ) {
      warnOnce(
        `x-axis-tick-count-category-noop-${chartName}`,
        `[hen-ui/${chartName}] xAxisTickCount has no effect when xAxisType="category" (the default) — Recharts emits one tick per category and ignores tickCount. Use xAxisInterval to thin labels on category axes, or switch to xAxisType="number" with numeric data.`,
      );
    }

    // Conflict warn — only when the consumer actually has a *usable*
    // tickCount that will win. If `rawTickCount` is non-finite, the pure
    // `validateXAxisTickCount` collapses it to `undefined`, so the
    // resolved config will USE `xAxisInterval` (not ignore it) and the
    // "tickCount takes precedence" message would be a lie. The dedicated
    // non-finite warn above already tells the operator their tickCount
    // was dropped.
    //
    // Also skip the conflict warn on non-numeric axes — the dedicated
    // category-noop warn above already tells the operator their tickCount
    // is being ignored, so adding "tickCount takes precedence" on top
    // would actively mislead.
    if (
      rawTickCount !== undefined &&
      Number.isFinite(rawTickCount) &&
      rawInterval !== undefined &&
      xAxisType === 'number'
    ) {
      warnOnce(
        `x-axis-config-conflict-${chartName}`,
        `[hen-ui/${chartName}] Both xAxisTickCount and xAxisInterval are set. xAxisTickCount takes precedence; xAxisInterval will be ignored.`,
      );
    }
  }, [rawTickCount, rawInterval, xAxisType, chartName]);
}

/**
 * Consolidated X-axis configuration hook. Resolves the four interlocking
 * pieces of EDUN-12033 wiring (`type`, `domain`, `tickCount`, `interval`)
 * into a single object ready to spread onto a Recharts `<XAxis>`, and emits
 * any associated dev-warnings via `useXAxisConfigWarnings`.
 *
 * Resolution rules — Recharts 3.x runtime behavior is axis-type-specific:
 *
 *   - `xAxisType='number'`   → `tickCount` works (drives "nice" tick
 *                              positions); `interval` is ignored. The
 *                              resolver zeros out `interval` so the prop
 *                              surface stays honest.
 *   - `xAxisType='category'` → `interval` works (thins which labels are
 *                              shown); `tickCount` is ignored (Recharts
 *                              always emits one tick per category). The
 *                              resolver preserves the back-compat
 *                              `interval={0}` default (show every label)
 *                              so existing charts keep rendering
 *                              identically — pre-EDUN-12033 BarChart /
 *                              LineChart hardcoded `interval={0}`.
 *
 * `useXAxisConfigWarnings` (called inside) emits the "x has no effect on
 * y" warns so consumers learn which prop applies to which axis without
 * having to reverse-engineer Recharts.
 *
 * Used by both LineChart and BarChart (vertical orientation) so these
 * axis-type rules live in exactly one place.
 */
export function useXAxisConfig({
  rawTickCount,
  rawInterval,
  xAxisType = 'category',
  chartName,
}: {
  rawTickCount: number | undefined;
  rawInterval: XAxisInterval | undefined;
  xAxisType?: 'category' | 'number';
  chartName: string;
}): {
  type: 'category' | 'number';
  /**
   * `['dataMin', 'dataMax']` on numeric axes (so points fill the axis
   * instead of being crammed into a sliver when values are far from 0);
   * `undefined` on category axes (Recharts derives the band domain).
   */
  domain: ['dataMin', 'dataMax'] | undefined;
  tickCount: number | undefined;
  interval: XAxisInterval | undefined;
} {
  useXAxisConfigWarnings({ rawTickCount, rawInterval, xAxisType, chartName });
  const tickCount = validateXAxisTickCount(rawTickCount);
  const validatedInterval = validateXAxisInterval(rawInterval);

  // Resolution rules (per Recharts 3.x runtime behavior, verified
  // empirically — see EDUN-12033 PR notes):
  //
  //   xAxisType='number'   → Recharts derives ticks from `tickCount` and
  //                          IGNORES `interval`. Zero out interval so the
  //                          prop surface stays honest; the numeric-noop
  //                          warn in `useXAxisConfigWarnings` tells the
  //                          operator about it.
  //   xAxisType='category' → Recharts IGNORES `tickCount` (one tick per
  //                          category, always). Keep the back-compat
  //                          `interval={0}` default (or the caller's
  //                          explicit interval) so labels render as the
  //                          consumer expects. If we dropped interval to
  //                          undefined here, Recharts' own default
  //                          `interval='preserveEnd'` would visibly thin
  //                          labels — looking like `tickCount` is working
  //                          when it isn't. Category-noop warn fires for
  //                          the dropped `tickCount` separately.
  //
  // For invalid `rawInterval` (e.g. 'bogus'), `validatedInterval` is
  // already `undefined` and the invalid-interval warn has fired — let
  // Recharts pick its own default.
  const interval =
    xAxisType === 'number'
      ? undefined
      : rawInterval === undefined
        ? 0
        : validatedInterval;

  return {
    type: xAxisType,
    domain: xAxisType === 'number' ? ['dataMin', 'dataMax'] : undefined,
    tickCount,
    interval,
  };
}

/**
 * Render an array of `ReferenceLineConfig[]` as Recharts `<ReferenceLine>`
 * elements. Handles the value-vs-category axis mapping based on the host
 * chart's orientation, so callers just pass `axis: 'value' | 'category'`
 * and the helper emits the correct `x=` / `y=` prop.
 *
 * Orientation semantics:
 *   - LineChart / vertical BarChart: value axis = Y, category axis = X
 *   - horizontal BarChart: value axis = X, category axis = Y (axes swap)
 *
 * Defaults (dashed stroke, neutral-600 color, width 1) match the design
 * intent that reference lines read as annotations, not data.
 *
 * Returns `null` when `lines` is empty/undefined so callers can drop the
 * result directly into JSX without a conditional.
 */
// Magnitude-relative "nice" step multipliers — the same set d3-scale uses.
// Kept short so the picker is deterministic and easy to reason about; any
// addition here changes tick rendering across every numeric chart in the
// library, so resist expanding without a concrete need.
const NICE_STEP_MULTIPLIERS = [1, 2, 2.5, 5] as const;

// Target tick-count window. Bounds chosen so a default chart at any
// reasonable span produces between 5 and 9 ticks — fewer feels sparse,
// more crowds the axis. The picker walks magnitudes upward and returns
// the first step inside the window.
const TARGET_TICK_MIN = 5;
const TARGET_TICK_MAX = 9;

/**
 * Pick a "nice" tick step (one of `[1, 2, 2.5, 5] × 10ⁿ`) so the resulting
 * tick count for `span` lands in `[TARGET_TICK_MIN, TARGET_TICK_MAX]`.
 *
 * Why this exists: Recharts' niceScale uses a fixed `tickCount=5` and
 * applies its own step-rounding heuristic via `getAdaptiveStep`. For a
 * small integer span (e.g. dataMax=3) the math collapses to `roughStep =
 * 3 / (5 - 1) = 0.75`, producing tick positions `[0, 0.75, 1.5, 2.25, 3]`.
 * Any rounding tick formatter (`(v) => Math.round(v).toLocaleString()`)
 * then maps those to `[0, 1, 1, 2, 2, 3]` — visible duplicate labels
 * (EDUN-11255 Bug 2). This picker bypasses that path by selecting the
 * step ourselves and handing Recharts an explicit `ticks` array.
 *
 * Algorithm: walks magnitudes from a few decades below the obvious choice
 * upward, trying each multiplier in order. Returns the first `step` whose
 * implied count fits the window. Falls through to a magnitude-rounded
 * upper bound for spans far outside the lookup range.
 */
export function pickNiceStep(span: number): number {
  if (!Number.isFinite(span) || span <= 0) return 1;

  // `span / TARGET_TICK_MAX` is the lower bound for an in-window step;
  // taking floor(log10) of that and stepping one decade lower catches
  // multiplier choices smaller than 1 at the right magnitude (e.g. 0.2,
  // 0.25, 0.5 at exp=-1).
  const startExponent = Math.floor(Math.log10(span / TARGET_TICK_MAX)) - 1;

  for (let exp = startExponent; exp <= startExponent + 6; exp++) {
    const magnitude = Math.pow(10, exp);
    for (const mult of NICE_STEP_MULTIPLIERS) {
      const step = mult * magnitude;
      const count = Math.ceil(span / step) + 1;
      if (count >= TARGET_TICK_MIN && count <= TARGET_TICK_MAX) return step;
    }
  }
  // Should be unreachable for finite spans but keeps the type total.
  return Math.pow(10, Math.ceil(Math.log10(span)));
}

/**
 * Configuration for a chart's value axis: pinned domain plus an explicit
 * tick array. Both come from {@link deriveValueAxisDefaults}.
 */
export interface ValueAxisDefaults {
  /** Inclusive `[min, max]` for the value axis. */
  domain: [number, number];
  /** Tick positions, evenly spaced by the picked nice step. */
  ticks: number[];
}

/**
 * Compute domain + tick positions for a chart's value axis from observed
 * data. Replaces Recharts' niceScale + auto-domain combo for the two
 * defects EDUN-11255 surfaced:
 *
 *   - Bug 1 — Recharts' default `['auto', 'auto']` resolves to
 *     `[dataMin, dataMax]`, which makes the smallest-value bar render
 *     with zero length. This helper anchors the monotonic case at zero
 *     so any positive value renders proportionally.
 *   - Bug 2 — Recharts' default `tickCount=5` produces a fractional
 *     `roughStep` for small integer spans, and a rounding tick formatter
 *     collapses the resulting fractional positions into duplicate
 *     labels. {@link pickNiceStep} guarantees an integer or "nice"
 *     fractional step, eliminating the duplicate-label class entirely.
 *
 * Contract:
 *   - Returns `undefined` for empty input or a single-valued series.
 *     Recharts handles those edge cases acceptably and the helper would
 *     have to invent a span out of thin air; better to defer.
 *   - Monotonic axes (Bar, Line) anchor at `Math.min(0, dataMin)` so a
 *     dataset that includes negative values still surfaces the negatives.
 *   - Signed axes (Waterfall) keep the data-driven min so the diverging
 *     view stays symmetric to the data.
 *   - Consumer-supplied `yAxisDomain` / `yAxisTicks` always win at the
 *     call site — this helper only fills the gap.
 */
export function deriveValueAxisDefaults(
  values: number[],
  options: { signed?: boolean } = {},
): ValueAxisDefaults | undefined {
  const finiteValues = values.filter((v) => Number.isFinite(v));
  if (finiteValues.length === 0) return undefined;

  const dataMin = Math.min(...finiteValues);
  const dataMax = Math.max(...finiteValues);

  // Monotonic axes communicate magnitude from a baseline, so the value
  // axis must include zero. Diverging axes keep the data-driven bounds.
  const rawMin = options.signed ? dataMin : Math.min(0, dataMin);
  const rawMax = options.signed ? dataMax : Math.max(0, dataMax);
  const span = rawMax - rawMin;

  // Single-valued series (after the zero-anchor for monotonic) — defer to
  // Recharts. Picking a nice step here would require fabricating a range.
  if (span === 0) return undefined;

  const step = pickNiceStep(span);
  const tickMin = Math.floor(rawMin / step) * step;
  const tickMax = Math.ceil(rawMax / step) * step;

  const ticks: number[] = [];
  // Floating-point summation can overshoot tickMax by ~1e-15 and silently
  // drop the final tick. The 1e-9 epsilon is generous enough to cover any
  // accumulated drift inside the loop without admitting spurious ticks.
  for (let v = tickMin; v <= tickMax + 1e-9; v += step) {
    // toFixed(10)→Number scrubs accumulated drift on the tick value
    // itself (e.g. `0.1 + 0.2 = 0.30000000000000004`).
    ticks.push(Number(v.toFixed(10)));
  }

  return { domain: [tickMin, tickMax], ticks };
}

export function renderReferenceLines(
  lines: ReferenceLineConfig[] | undefined,
  chartOrientation: 'vertical' | 'horizontal',
): React.ReactNode {
  if (!lines?.length) return null;

  return lines.map((line) => {
    const axis = line.axis ?? 'value';
    const isHorizontal = chartOrientation === 'horizontal';

    const targetsValueAxis = axis === 'value';
    const targetsYAxis = isHorizontal ? !targetsValueAxis : targetsValueAxis;

    const axisProps = targetsYAxis ? { y: line.at } : { x: line.at };

    // Composite-signature key (no array index): stable across reorders and
    // does not force a remount when the caller reshuffles the array. The
    // `(axis, at, label)` triple is the identity of a reference line from
    // the consumer's POV; two lines sharing that triple are semantic
    // duplicates and would collide here, which is the correct behaviour
    // (React will warn and the second one drops — surface the bug rather
    // than silently fan out identical annotations).
    return (
      <ReferenceLine
        key={`ref-${axis}-${line.at}-${line.label ?? ''}`}
        {...axisProps}
        stroke={line.stroke ?? 'var(--color-neutral-600)'}
        strokeDasharray={line.strokeDasharray ?? '4 4'}
        strokeWidth={line.strokeWidth ?? 1}
        label={
          line.label
            ? {
                value: line.label,
                position: 'insideTopRight',
                fill: line.stroke ?? 'var(--color-neutral-600)',
                fontSize: 11,
              }
            : undefined
        }
        ifOverflow='extendDomain'
      />
    );
  });
}

/**
 * Renders a truncated axis label with a Tooltip that reveals the full text on
 * hover.
 *
 * Positioning approach: `getBoundingClientRect` is overridden on the SVG
 * `<text>` element to return a zero-size rect at the exact `(nx, ny)` anchor
 * point, converted from SVG user-space to viewport coordinates via
 * `ownerSVGElement.getScreenCTM()`. This is necessary because:
 *
 * - A non-rotated `<text>` `getBoundingClientRect` covers the glyph ink box —
 *   fine for horizontal labels but still wider than the single anchor point
 *   we want.
 * - A rotated `<text>` `getBoundingClientRect` returns the axis-aligned
 *   bounding box of the swept glyphs — a large tilted rectangle that makes
 *   the tooltip appear offset from the label.
 *
 * By anchoring at `(nx, ny)` — the point Recharts uses as the tick mark —
 * the tooltip always appears to the right of that point, into the chart area,
 * regardless of whether the label is rotated or horizontal.
 *
 * `getScreenCTM()` on the SVG root accounts for every ancestor transform
 * (SVG attribute, CSS transform, scroll, zoom) so the result is always the
 * correct viewport position.
 *
 * Kept private (not exported) — hooks are required for the ref/effect pattern,
 * while `renderChartTick` must remain a plain function for Recharts' `tick`
 * render-prop.
 */
type SvgTextAnchor = React.SVGAttributes<SVGTextElement>['textAnchor'];

interface ChartTickWithTooltipProps {
  nx: number;
  ny: number;
  dy: string;
  textAnchor: SvgTextAnchor;
  rotate: boolean;
  label: string;
  short: string;
}

function ChartTickWithTooltip({
  nx,
  ny,
  dy,
  textAnchor,
  rotate,
  label,
  short,
}: ChartTickWithTooltipProps): React.ReactElement {
  const textRef = React.useRef<SVGTextElement>(null);

  // Override getBoundingClientRect so Floating UI anchors the tooltip at the
  // single (nx, ny) tick point rather than the swept bounding box of the text
  // glyphs (which is large when the label is rotated).
  React.useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return;
    el.getBoundingClientRect = (): DOMRect => {
      const svg = el.ownerSVGElement;
      if (!svg) return new DOMRect(0, 0, 0, 0);
      try {
        const ctm = svg.getScreenCTM();
        if (!ctm) return new DOMRect(0, 0, 0, 0);
        const pt = svg.createSVGPoint();
        pt.x = nx;
        pt.y = ny;
        const screen = pt.matrixTransform(ctm);
        return new DOMRect(screen.x, screen.y, 0, 0);
      } catch {
        return new DOMRect(0, 0, 0, 0);
      }
    };
  }, [nx, ny]);

  return (
    <ShadcnTooltip>
      <ShadcnTooltipTrigger asChild>
        <text
          ref={textRef}
          x={nx}
          y={ny}
          dy={dy}
          textAnchor={textAnchor}
          transform={rotate ? `rotate(-45, ${nx}, ${ny})` : undefined}
          tabIndex={0}
          className='text-l1 cursor-help fill-current focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400'
        >
          {short}
        </text>
      </ShadcnTooltipTrigger>
      <ShadcnTooltipContent side='right'>{label}</ShadcnTooltipContent>
    </ShadcnTooltip>
  );
}

/**
 * Render a single Recharts axis tick, truncating labels that exceed
 * `MAX_LABEL_LENGTH_BEFORE_TRUNCATION` and wrapping truncated labels in a
 * `Tooltip` so the full text is still accessible on hover.
 *
 * Extracted here (alongside `renderReferenceLines`) so all chart composites
 * share a single implementation.
 *
 * @param props.x - X coordinate supplied by Recharts.
 * @param props.y - Y coordinate supplied by Recharts.
 * @param props.payload - Recharts payload object; the label is at `payload.value`.
 * @param props.dy - SVG baseline offset. `'0.71em'` for X-axis (default); `'0.355em'` for Y-axis.
 * @param props.textAnchor - SVG text-anchor. `'middle'` for X-axis (default); `'end'` for Y-axis.
 * @param props.rotate - When `true`, the label is rotated -45° around its anchor point.
 *
 * Usage — X-axis:
 *   `<XAxis tick={(p) => renderChartTick({ ...p, rotate: needsRotation })} />`
 *
 * Usage — Y-axis, horizontal chart:
 *   `<YAxis tick={(p) => renderChartTick({ ...p, dy: '0.355em', textAnchor: 'end' })} />`
 */
interface RenderChartTickProps {
  x?: number | string;
  y?: number | string;
  payload?: { value?: unknown };
  /**
   * Tick index passed by Recharts (`0` for the first tick, `1` for the
   * second, etc.). Forwarded to `tickFormatter` so consumers can apply
   * positional formatting (e.g. show units only on the first tick).
   * Recharts always provides this when invoking the `tick` render prop.
   */
  index?: number;
  dy?: string;
  textAnchor?: SvgTextAnchor;
  rotate?: boolean;
  maxLabelLength?: number;
  /**
   * Optional formatter applied to the tick value before display/truncation.
   *
   * Signature accepts a second `index` arg to stay compatible with Recharts'
   * own `TickFormatter` type — when callers spread `...p` from Recharts'
   * tick props into `renderChartTick`, the Recharts `tickFormatter` leaks
   * through and must satisfy this contract.
   */
  tickFormatter?: (value: number | string, index: number) => string;
}

export function renderChartTick({
  x = 0,
  y = 0,
  payload,
  index = 0,
  dy = '0.71em',
  textAnchor = 'middle',
  rotate = false,
  maxLabelLength,
  tickFormatter,
}: RenderChartTickProps): React.ReactElement {
  if (
    process.env.NODE_ENV !== 'production' &&
    maxLabelLength != null &&
    maxLabelLength <= 0
  ) {
    console.warn(
      `[hen-ui/renderChartTick] maxLabelLength=${maxLabelLength} is not valid (must be ≥ 4). Falling back to the library default (${MAX_LABEL_LENGTH_BEFORE_TRUNCATION}).`,
    );
  }

  const effectiveMax =
    maxLabelLength != null && maxLabelLength >= 4
      ? maxLabelLength
      : MAX_LABEL_LENGTH_BEFORE_TRUNCATION;

  const nx = +x;
  const ny = +y;
  const rawValue = payload?.value ?? '';
  // Apply formatter if provided, then convert to string for truncation.
  // The `index` arg is the real tick position forwarded from Recharts' tick
  // render-prop, enabling positional formatters (e.g. show units on first
  // tick only).
  const label =
    tickFormatter &&
    (typeof rawValue === 'string' || typeof rawValue === 'number')
      ? tickFormatter(rawValue, index)
      : String(rawValue);
  const short = truncateWithEllipsis(label, effectiveMax);
  const isTruncated = short !== label;

  if (!isTruncated) {
    return (
      <text
        x={nx}
        y={ny}
        dy={dy}
        textAnchor={textAnchor}
        transform={rotate ? `rotate(-45, ${nx}, ${ny})` : undefined}
        className='text-l1 fill-current'
      >
        {short}
      </text>
    );
  }

  // ShadcnTooltipProvider must be co-located here because each tick is
  // rendered independently inside Recharts' SVG subtree — there is no
  // shared React tree above it that already provides a Radix TooltipProvider.
  return (
    <ShadcnTooltipProvider>
      <ChartTickWithTooltip
        nx={nx}
        ny={ny}
        dy={dy}
        textAnchor={textAnchor}
        rotate={rotate}
        label={label}
        short={short}
      />
    </ShadcnTooltipProvider>
  );
}

// ─── Tooltip enhancement helpers (EDUN-12039) ────────────────────────────────

const VALID_TOOLTIP_MODES = new Set<string>(['hover', 'pinned', 'disabled']);

/**
 * Validates `tooltipMode` at runtime and returns a safe `ChartTooltipMode`.
 */
export function validateTooltipMode(
  mode: string | undefined,
): ChartTooltipMode {
  if (!mode) return 'hover';
  if (VALID_TOOLTIP_MODES.has(mode)) return mode as ChartTooltipMode;
  return 'hover';
}

/**
 * Parses a {@link ChartTooltipFooter} value into a React node suitable for
 * rendering inside the tooltip as a footnote.
 *
 * - Plain strings are rendered verbatim.
 * - `{ md: string }` values are parsed for `**bold**`, `*italic*`, and
 *   `[text](url)` — the minimal subset needed for methodology copy.
 *   Absolute URLs are validated via `new URL`; relative URLs (`/path`,
 *   `./path`, etc.) are passed through as-is. Anything with an unsafe
 *   protocol falls back to `'#'`. Absolute http(s) links open in a new
 *   tab with `rel="noopener noreferrer"`. Relative links do not.
 *
 * Returns `null` for empty / undefined input so callers can drop the result
 * directly into JSX without a conditional.
 */
export function renderTooltipFooter(
  content: ChartTooltipFooter | undefined,
): React.ReactNode {
  if (!content) return null;
  const raw = typeof content === 'string' ? content : content.md;
  if (!raw.trim()) return null;

  if (typeof content === 'string') {
    return (
      <p className='mt-2 border-t border-neutral-200 pt-2 text-xs text-neutral-500'>
        {content}
      </p>
    );
  }

  // Minimal Markdown parser: split on bold, italic, and link tokens.
  const nodes = parseMinimalMarkdown(raw, {
    linkClassName: MINIMAL_MARKDOWN_LINK_CLASS_NAME,
  });

  return (
    <p className='mt-2 border-t border-neutral-200 pt-2 text-xs text-neutral-500'>
      {nodes}
    </p>
  );
}

/**
 * Manages outside-click and Escape-key dismissal for `tooltipMode="pinned"`.
 *
 * Recharts' `trigger="click"` shows the tooltip when the user clicks a data
 * point but has no built-in "dismiss when clicking outside the chart" logic.
 * This hook fills that gap:
 *
 * - Attaches a `document.pointerdown` listener in the capture phase. Using
 *   `pointerdown` (rather than `mousedown`) covers both mouse and touch
 *   without needing a separate `touchstart` handler. The capture phase
 *   ensures the listener fires even if an inner component calls
 *   `stopPropagation()` on the event during bubbling. If the pointer lands
 *   outside the element referenced by `containerRef`, it sets `tooltipActive`
 *   to `false`, which Recharts respects as a controlled override to hide the
 *   tooltip.
 * - Attaches a `keydown` listener that does the same on Escape.
 * - `onChartClick` — call from the Recharts chart's `onClick` prop. It resets
 *   the dismissed state back to `undefined` so Recharts regains control on
 *   the very next data-point click.
 * - `containerRef` — attach to the chart's outermost wrapper `<div>`.
 *
 * When `tooltipMode` is not `"pinned"`, everything is a no-op and
 * `tooltipActive` is always `undefined` (Recharts controls visibility).
 */
export function usePinnedTooltip(mode: ChartTooltipMode | undefined): {
  /** Pass to `<ChartTooltip active={...}>`. `false` = force-hidden; `undefined` = Recharts controls. */
  tooltipActive: boolean | undefined;
  /** Pass to the Recharts chart `onClick` prop to re-arm the tooltip after a dismiss. */
  onChartClick: () => void;
  /** Attach to the outermost chart wrapper `<div>` for outside-click detection. */
  containerRef: React.MutableRefObject<HTMLDivElement | null>;
} {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [dismissed, setDismissed] = React.useState(false);

  // Clear dismissed state whenever the mode changes so stale hidden state
  // does not carry over when the operator switches tooltipMode at runtime.
  React.useEffect(() => {
    setDismissed(false);
  }, [mode]);

  React.useEffect(() => {
    if (mode !== 'pinned') return;

    const handlePointerDown = (e: PointerEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setDismissed(true);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDismissed(true);
    };

    // Capture phase: fires before bubbling so stopPropagation() in inner
    // components cannot prevent this handler from running.
    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [mode]);

  const onChartClick = React.useCallback(() => {
    // Re-arm: clear the dismissed flag so Recharts regains control and can
    // show the tooltip for the newly-clicked data point.
    if (mode === 'pinned') setDismissed(false);
  }, [mode]);

  return {
    tooltipActive: mode === 'pinned' && dismissed ? false : undefined,
    onChartClick,
    containerRef,
  };
}

// ── Interactive legend shared hook ────────────────────────────────────────────

export interface UseInteractiveLegendOptions {
  /** Series keys for the chart */
  series: readonly string[];
  /** Whether the legend is shown */
  showLegend: boolean;
  /** Whether the legend is interactive */
  clickableLegend: boolean;
  /** Chart name for warning messages (e.g., 'BarChart', 'LineChart') */
  chartName: string;
}

export interface UseInteractiveLegendResult {
  /** Set of currently hidden series keys */
  hiddenSeries: ReadonlySet<string>;
  /** Callback to toggle a series visibility */
  handleSeriesToggle: (key: string) => void;
}

/**
 * Shared hook for interactive legend behavior across chart composites.
 * Manages hidden series state and enforces the "cannot hide last visible
 * series" guard. Emits a one-time warning when `clickableLegend` is enabled
 * without `showLegend`.
 *
 * @internal
 */
export function useInteractiveLegend(
  options: UseInteractiveLegendOptions,
): UseInteractiveLegendResult {
  const { series, showLegend, clickableLegend, chartName } = options;

  // Guard: showLegend=false + clickableLegend=true is a misconfiguration.
  // Moved to useEffect to keep render phase pure (avoids side effects during
  // render and duplicate warnings in React StrictMode).
  React.useEffect(() => {
    if (!showLegend && clickableLegend) {
      warnOnce(
        `${chartName}/clickableLegend-no-legend`,
        `[hen-ui/${chartName}] \`clickableLegend\` is true but \`showLegend\` is false — no legend will be rendered. Set \`showLegend={true}\` or remove \`clickableLegend\`.`,
      );
    }
  }, [showLegend, clickableLegend, chartName]);

  // Track which series are hidden by the user via the interactive legend.
  // State is component-local and resets on unmount (acceptable per AC).
  const [hiddenSeries, setHiddenSeries] = React.useState<Set<string>>(
    () => new Set(),
  );

  const handleSeriesToggle = React.useCallback(
    (key: string) => {
      setHiddenSeries((prev) => {
        // Guard: cannot hide the last visible series.
        const visible = series.filter((k) => !prev.has(k));
        if (visible.length === 1 && visible[0] === key) return prev;

        const next = new Set(prev);
        if (next.has(key)) {
          next.delete(key);
        } else {
          next.add(key);
        }
        return next;
      });
    },
    [series],
  );

  return { hiddenSeries, handleSeriesToggle };
}

// ─────────────────────────────────────────────────────────────────────────────
// Scatter-chart pure helpers — live here so ScatterChart.tsx contains no
// `__`-prefixed test-only re-exports on its public surface, while still
// being directly unit-testable via this module.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Minimal item shape Recharts passes through `Tooltip.content` callbacks for
 * a scatter chart. Only the four fields needed by
 * {@link resolveScatterTooltipBorderColor} are captured; coordinate / viewBox
 * metadata is excluded so the type is reusable in unit tests that exercise the
 * resolver without mounting a full Recharts tree.
 */
export type ScatterTooltipPayloadItem = {
  color?: string;
  dataKey?: string | number;
  value?: unknown;
  payload?: Record<string, unknown>;
};

/**
 * Resolve the active series's color for the scatter tooltip's outline.
 * Priority — matches the same order `ChartTooltipContent` uses internally
 * for indicator color so per-point `colorRules` / diverging-arm fills stay
 * reflected even after we drop the per-row swatches:
 *
 *   1. `head.payload.fill`  — per-point fill (rules / diverging arms)
 *   2. `indicatorResolver`  — semantic / color-rule result
 *   3. `head.color`         — Recharts-injected series base color
 *   4. `undefined`          — neutral border (no payload / no match)
 */
export function resolveScatterTooltipBorderColor(
  payload: ReadonlyArray<ScatterTooltipPayloadItem> | undefined,
  indicatorResolver:
    | ((item: {
        dataKey?: string | number | ((obj: unknown) => unknown);
        value?: unknown;
        color?: string;
      }) => string | undefined)
    | undefined,
): string | undefined {
  const head = payload?.[0];
  if (!head) return undefined;
  const perPointFill =
    head.payload && typeof head.payload.fill === 'string'
      ? head.payload.fill
      : undefined;
  const semanticOrRule = indicatorResolver?.({
    dataKey: head.dataKey,
    value: head.value,
    color: head.color,
  });
  return perPointFill ?? semanticOrRule ?? head.color;
}

/**
 * Even-stride downsampler for scatter chart series. A single
 * `stride = ceil(total / maxPoints)` is derived from the grand total and
 * applied **within each series** — keeping every `stride`-th point — so
 * every series stays visible and the distribution shape is preserved rather
 * than biasing to the first N points. Returns the input array reference
 * unchanged when the set already fits under `maxPoints` (or `maxPoints` is
 * non-positive), so the common path allocates nothing.
 *
 * @param series    - Per-series data arrays (already coerced / sanitized).
 * @param total     - Grand total of valid points across all series.
 * @param maxPoints - Maximum number of points to render across all series.
 */
export function downsampleScatterSeries(
  series: ScatterChartSeries[],
  total: number,
  maxPoints: number,
): ScatterChartSeries[] {
  if (!Number.isFinite(maxPoints) || maxPoints <= 0) return series;
  if (total <= maxPoints) return series;
  const stride = Math.ceil(total / maxPoints);
  if (stride <= 1) return series;
  return series.map((s) => {
    const kept: ScatterChartDatum[] = [];
    for (let i = 0; i < s.data.length; i += stride) {
      kept.push(s.data[i]);
    }
    return { key: s.key, data: kept };
  });
}
